import React, { useState, useEffect, useMemo } from 'react';
import { timesheetAPI, commesseAPI, risorseAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { StatusBadge } from '../components/common/StatusBadge';
import { TrasfertaSection } from '../components/common/TrasfertaSection';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  Calendar,
  Check,
  X,
  Loader2,
  Trash2,
  Pencil,
  AlertCircle
} from 'lucide-react';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const formatDateStr = (date) => {
  return date.toISOString().split('T')[0];
};

const formatDisplayDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
};

const getStatusColor = (stato) => {
  switch (stato) {
    case 'approvato': return 'bg-emerald-500';
    case 'completo': return 'bg-blue-500';
    case 'incompleto': return 'bg-slate-300 dark:bg-slate-600';
    default: return 'bg-slate-300';
  }
};

export const TimesheetPage = () => {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [weekSummary, setWeekSummary] = useState(null);
  const [commesse, setCommesse] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    data: '',
    commessa_id: '',
    fase_id: '',
    ore: '',
    note: ''
  });

  // Selected commessa for phases
  const selectedCommessa = useMemo(() => {
    return commesse.find(c => c.id === formData.commessa_id);
  }, [commesse, formData.commessa_id]);

  useEffect(() => {
    loadData();
  }, [weekStart]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [weekRes, commesseRes] = await Promise.all([
        timesheetAPI.getWeekSummary(formatDateStr(weekStart)),
        commesseAPI.getAll()
      ]);
      setWeekSummary(weekRes.data);
      setCommesse(commesseRes.data.filter(c => c.stato === 'attiva'));
    } catch (error) {
      console.error('Error loading timesheet data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    setWeekStart(getWeekStart(new Date()));
  };

  const openAddDialog = (dayData = null) => {
    setEditingEntry(null);
    setFormData({
      data: dayData?.data || formatDateStr(new Date()),
      commessa_id: '',
      fase_id: '',
      ore: '',
      note: ''
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      data: entry.data,
      commessa_id: entry.commessa_id,
      fase_id: entry.fase_id || '',
      ore: entry.ore.toString(),
      note: entry.note || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.commessa_id || !formData.ore) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        data: formData.data,
        commessa_id: formData.commessa_id,
        fase_id: formData.fase_id || null,
        ore: parseFloat(formData.ore),
        note: formData.note || null
      };

      if (editingEntry) {
        await timesheetAPI.update(editingEntry.id, submitData);
        toast.success('Ore aggiornate');
      } else {
        await timesheetAPI.create(submitData);
        toast.success('Ore inserite');
      }

      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving timesheet:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa registrazione?')) return;
    
    try {
      await timesheetAPI.delete(entryId);
      toast.success('Registrazione eliminata');
      loadData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="timesheet-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Timesheet</h1>
          <p className="text-muted-foreground">Registra le ore lavorate giornalmente</p>
        </div>
        <Button onClick={() => openAddDialog()} data-testid="add-timesheet-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuova Registrazione
        </Button>
      </div>

      {/* Week Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => navigateWeek(-1)} data-testid="prev-week-btn">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
                Oggi
              </Button>
              <div className="text-center">
                <p className="font-semibold">
                  {new Date(weekStart).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })} - {' '}
                  {new Date(new Date(weekStart).setDate(weekStart.getDate() + 6)).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <p className="text-sm text-muted-foreground">
                  Totale settimana: <span className="font-mono font-semibold">{weekSummary?.ore_totali || 0}h</span> / 40h
                </p>
              </div>
            </div>
            
            <Button variant="ghost" size="icon" onClick={() => navigateWeek(1)} data-testid="next-week-btn">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-2">
        {weekSummary?.giorni?.map((day, index) => {
          const isToday = day.data === formatDateStr(new Date());
          const isWeekend = index >= 5;
          
          return (
            <Card 
              key={day.data}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isToday ? 'ring-2 ring-primary' : ''
              } ${isWeekend ? 'opacity-75' : ''} ${
                selectedDay?.data === day.data ? 'bg-muted' : ''
              }`}
              onClick={() => setSelectedDay(day)}
              data-testid={`day-card-${day.data}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{WEEKDAYS[index]}</span>
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(day.stato)}`} />
                </div>
                <p className="text-xs text-muted-foreground mb-1">
                  {new Date(day.data).getDate()}
                </p>
                <p className="font-mono text-lg font-bold">{day.ore_totali}h</p>
                {day.entries?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {day.entries.length} {day.entries.length === 1 ? 'attività' : 'attività'}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Selected Day Details */}
      {selectedDay && (
        <Card className="animate-fade-in" data-testid="day-details">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {formatDisplayDate(selectedDay.data)}
              </CardTitle>
              <CardDescription>
                {selectedDay.ore_totali}h registrate
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => openAddDialog(selectedDay)} data-testid="add-entry-day-btn">
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi Ore
            </Button>
          </CardHeader>
          <CardContent>
            {selectedDay.entries?.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                Nessuna registrazione per questo giorno
              </p>
            ) : (
              <div className="space-y-3">
                {selectedDay.entries?.map((entry) => (
                  <div 
                    key={entry.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    data-testid={`entry-${entry.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm text-muted-foreground">
                          {entry.commessa_codice}
                        </span>
                        <Badge variant={entry.stato === 'approvato' ? 'default' : entry.stato === 'respinto' ? 'destructive' : 'secondary'}>
                          {entry.stato === 'approvato' ? 'Approvato' : entry.stato === 'respinto' ? 'Respinto' : 'In attesa'}
                        </Badge>
                      </div>
                      <p className="font-medium">{entry.commessa_nome}</p>
                      {entry.fase_nome && (
                        <p className="text-sm text-muted-foreground">Fase: {entry.fase_nome}</p>
                      )}
                      {entry.note && (
                        <p className="text-sm text-muted-foreground mt-1">{entry.note}</p>
                      )}
                      {entry.motivo_rifiuto && (
                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {entry.motivo_rifiuto}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-mono text-xl font-bold">{entry.ore}h</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(entry.costo_calcolato)}
                        </p>
                      </div>
                      {entry.stato !== 'approvato' && (
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(entry); }}
                            data-testid={`edit-entry-${entry.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                            data-testid={`delete-entry-${entry.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trasferte Section - visible when a day is selected */}
      {selectedDay && (
        <TrasfertaSection 
          selectedDate={selectedDay.data} 
          onUpdate={loadData}
        />
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="timesheet-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Modifica Registrazione' : 'Nuova Registrazione Ore'}
            </DialogTitle>
            <DialogDescription>
              Inserisci le ore lavorate per una commessa
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="data">Data *</Label>
              <Input
                id="data"
                type="date"
                value={formData.data}
                onChange={(e) => setFormData({...formData, data: e.target.value})}
                required
                data-testid="timesheet-data-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="commessa">Commessa *</Label>
              <Select 
                value={formData.commessa_id} 
                onValueChange={(value) => setFormData({...formData, commessa_id: value, fase_id: ''})}
              >
                <SelectTrigger data-testid="timesheet-commessa-select">
                  <SelectValue placeholder="Seleziona commessa" />
                </SelectTrigger>
                <SelectContent>
                  {commesse.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.codice_commessa} - {c.nome_progetto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedCommessa?.fasi?.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="fase">Fase</Label>
                <Select 
                  value={formData.fase_id || "none"} 
                  onValueChange={(value) => setFormData({...formData, fase_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="timesheet-fase-select">
                    <SelectValue placeholder="Seleziona fase (opzionale)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna fase</SelectItem>
                    {selectedCommessa.fasi.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="ore">Ore *</Label>
              <Input
                id="ore"
                type="number"
                min="0.5"
                max="24"
                step="0.5"
                value={formData.ore}
                onChange={(e) => setFormData({...formData, ore: e.target.value})}
                placeholder="Es. 8"
                required
                data-testid="timesheet-ore-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Descrizione attività svolta..."
                rows={3}
                data-testid="timesheet-note-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="timesheet-submit-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingEntry ? 'Salva Modifiche' : 'Registra Ore'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetPage;
